import { ErrorProduct, ProductCreation, ProductCreationWithErrors } from '@Arti-zen/package-backoffice'
import { Title } from 'modules/product/UploadProducts/ArtisanUploadProducts'
import { useState } from 'react'
import ProductListTable from '../ProductListTable'
import { Theme } from 'theme/theme'
import { isErrorCorrected } from '../product.helpers'
import styled from 'styled-components'
import FormProductError from '../errorListTable/FormProductError'
import ViewProducts from './ViewProducts'

type TProps = {
  products: ProductCreation[]
  setProducts: React.Dispatch<React.SetStateAction<ProductCreation[]>>
  productsWithErrors: ProductCreationWithErrors[]
  setProductsWithErrors: React.Dispatch<React.SetStateAction<ProductCreationWithErrors[]>>
  onSubmit: () => Promise<void>
  onModify: boolean
}

export default function TableProducts({
  products,
  productsWithErrors,
  setProducts,
  setProductsWithErrors,
  onSubmit,
  onModify,
}: TProps) {
  const [openedProductForm, setOpenedProductForm] = useState<ProductCreation | ProductCreationWithErrors | null>(null)
  const [indexProductForm, setIndexProductForm] = useState<number | null>(null)
  const [view, setView] = useState<'withErrors' | 'withoutErrors'>(
    productsWithErrors?.length > 0 ? 'withErrors' : 'withoutErrors'
  )

  const deleteProduct = ({ index, from }: { index: number; from: 'products' | 'productsWithErrors' }) => {
    const newList = from === 'products' ? [...products] : [...productsWithErrors]
    newList.splice(index, 1)
    from === 'products' ? setProducts(newList) : setProductsWithErrors(newList as ProductCreationWithErrors[])
    if (newList.length === 0 && from === 'productsWithErrors') setView('withoutErrors')
  }

  const deleteProductsWithErrorsWithoutProp = (prop: keyof ProductCreation) => {
    setProductsWithErrors((previous) => previous.filter((product) => !!product[prop]))
  }

  const replaceAllErrors = ({
    property,
    getValue,
    conditionInErr,
  }: {
    property: keyof ProductCreation
    getValue: (error: ErrorProduct) => any
    conditionInErr: (err: ErrorProduct) => boolean
  }) => {
    const changedProducts = []
    for (let i = productsWithErrors.length - 1; i >= 0; i--) {
      const product = productsWithErrors[i]
      //check if product has error in this property
      const error = product.errors.find((err) => conditionInErr(err))
      if (!error) {
        continue
      }
      changedProducts.push(product)
      correctProduct({ corrections: { [property]: getValue(error) }, product, index: i })
    }
  }

  const removeErrors = (product: Partial<ProductCreationWithErrors>): ProductCreation => {
    const { errors: _, ...productWithoutErrors } = product
    return productWithoutErrors as ProductCreation
  }

  const correctProduct = ({
    corrections,
    product,
    index,
  }: {
    corrections: Partial<ProductCreationWithErrors>
    product: ProductCreationWithErrors
    index: number
  }) => {
    const correctionsWithoutErrors = removeErrors(corrections)

    if (!product.errors) {
      setProducts((previous) => {
        const newProducts = [...previous]
        newProducts[index] = { ...product, ...correctionsWithoutErrors }
        return newProducts
      })
      return
    }

    const remainingErrors = product?.errors?.filter((error) => !isErrorCorrected(error, corrections))

    if (remainingErrors.length === 0) {
      setProducts((previousProducts) => [
        ...previousProducts,
        removeErrors({ ...product, ...correctionsWithoutErrors }),
      ])

      setProductsWithErrors((previous) => {
        const newProductsWithErrors = [...previous]
        newProductsWithErrors.splice(index, 1)
        if (newProductsWithErrors.length === 0) setView('withoutErrors')
        return newProductsWithErrors
      })

      return
    }

    setProductsWithErrors((previous) => {
      const newProductsWithErrors = [...previous]
      newProductsWithErrors[index] = { ...product, ...corrections, errors: remainingErrors }
      return newProductsWithErrors
    })
  }

  return (
    <>
      <Row style={{ justifyContent: 'flex-start' }}>
        <Tab selected={view === 'withErrors'} onClick={() => setView('withErrors')}>
          Produits avec erreurs ({productsWithErrors?.length ?? 0})
        </Tab>
        <Tab selected={view === 'withoutErrors'} onClick={() => setView('withoutErrors')}>
          Produits sans erreurs ({products?.length ?? 0})
        </Tab>
      </Row>

      {view === 'withErrors' && (
        <>
          <Title>Produits avec erreurs : {productsWithErrors?.length}</Title>
          <ProductListTable
            data={productsWithErrors}
            correctProduct={correctProduct}
            openForm={(product, index) => {
              setOpenedProductForm(product)
              setIndexProductForm(index)
            }}
            deleteProduct={(index: number) => deleteProduct({ index, from: 'productsWithErrors' })}
            onModify={onModify}
            replaceAllErrors={replaceAllErrors}
            deleteProductsWithErrorsWithoutProp={deleteProductsWithErrorsWithoutProp}
          />
        </>
      )}

      {view === 'withoutErrors' && (
        <ViewProducts
          products={products}
          correctProduct={correctProduct}
          openForm={(product, index) => {
            setOpenedProductForm(product)
            setIndexProductForm(index)
          }}
          deleteProduct={deleteProduct}
          onSubmit={onSubmit}
          disableButton={productsWithErrors?.length > 0 || products?.length === 0}
          onModify={onModify}
        />
      )}

      <FormProductError
        product={openedProductForm}
        onClose={() => {
          setOpenedProductForm(null)
          setIndexProductForm(null)
        }}
        correctProduct={(corrections) =>
          correctProduct({
            corrections,
            product: openedProductForm as ProductCreationWithErrors,
            index: indexProductForm,
          })
        }
      />
    </>
  )
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 15px;
`

const Tab = styled.div<{ selected: boolean }>`
  border: 1px solid ${Theme.colors.greenArtizen};
  background-color: ${({ selected }) => (selected ? Theme.colors.greenArtizen : 'white')};
  color: ${({ selected }) => (selected ? 'white' : Theme.colors.greenArtizen)};
  padding: 15px;
  border-radius: 5px;
  font-size: 13px;
  &:hover {
    cursor: pointer;
    background-color: ${({ selected }) => `#1edb97${selected ? '90' : '10'}`};
  }
`
